import React, { useEffect, useMemo, useState } from "react";
import {
  CCardHeader,
  CCol,
  CButton,
  CFormInput,
  CFormLabel,
  CRow,
  CFormSelect,
} from "@coreui/react";
import PropTypes from "prop-types";
import { Formik, Field, ErrorMessage, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { successAlert } from "src/utils/Swal";
import { addInterests, editIntereset } from "src/redux/actions/loanListAction";

const ProductAddForm = ({
  setShowAddForm,
  setShowAddButton,
  setShowSelectAndSearch,
  intial,
  edit,
}) => {
  const dispatch = useDispatch();
  const { addData, updateData, error } = useSelector(
    (state) => state?.interestsReducer
  );

  const [submission, setSubmission] = useState(false);

  const schemaName = sessionStorage.getItem("schemaName") || "";

  const closeForm = () => {
    setShowSelectAndSearch(true);
    setShowAddButton(true);
    setShowAddForm(false);
  };

  const initialValues = useMemo(
    () => ({
      loanType: intial?.loanType ?? "",
      rate: intial?.rate ?? "",
      price: intial?.price ?? "",
      down_payment: intial?.down_payment ?? "",
      // ✅ new fields
      is_active:
        intial?.is_active === 0 || intial?.is_active === "0"
          ? "0"
          : intial?.is_active === 1 || intial?.is_active === "1"
            ? "1"
            : "1", // default active
      file_charges: intial?.file_charges ?? "",
      other_charges: intial?.other_charges ?? "",
      schemaName,
      id: intial?.id ?? "",
    }),
    [intial, schemaName]
  );

  const validationSchema = Yup.object().shape({
    loanType: Yup.string().trim().required("Loan Type is required"),
    price: Yup.number()
      .typeError("Price must be a number")
      .min(0, "Price cannot be negative")
      .required("Price is required"),
    down_payment: Yup.number()
      .typeError("Down Payment must be a number")
      .min(0, "Down Payment cannot be negative")
      .required("Down Payment is required"),
    rate: Yup.number()
      .typeError("Rate must be a number")
      .min(0, "Rate cannot be negative")
      .required("Rate is required"),

    // ✅ new fields validation
    is_active: Yup.string().oneOf(["0", "1"]).required("Status is required"),
    file_charges: Yup.number()
      .typeError("File Charges must be a number")
      .min(0, "File Charges cannot be negative")
      .required("File Charges is required"),
    other_charges: Yup.number()
      .typeError("Other Charges must be a number")
      .min(0, "Other Charges cannot be negative")
      .required("Other Charges is required"),
  });

  // Show success only when reducer confirms success (works for both add/edit)
  useEffect(() => {
    if (error === false) {
      if (!submission && addData?.status === "success") {
        successAlert("Success", "Product has been successfully created");
        setSubmission(true);
        closeForm();
      }
      if (!submission && updateData?.status === "success") {
        successAlert("Success", "Product has been successfully updated");
        setSubmission(true);
        closeForm();
      }
    }
  }, [addData, updateData, error, submission]);

  const handleSubmit = (values) => {
    setSubmission(false);

    // normalize payload (optional but helpful)
    const payload = {
      ...values,
      // ensure numeric values are sent as numbers
      price: Number(values.price),
      down_payment: Number(values.down_payment),
      rate: Number(values.rate),
      file_charges: Number(values.file_charges),
      other_charges: Number(values.other_charges),
      is_active: Number(values.is_active), // backend may expect 0/1
    };

    if (edit) {
      dispatch(editIntereset(payload));
      return;
    }
    dispatch(addInterests(payload));
  };

  return (
    <CRow>
      <CCardHeader className="custom-card-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h5 className="m-0">{edit ? "Edit Product" : "Add Product"}</h5>
          <button
            type="button"
            className="custom-close-btn"
            aria-label="Close"
            onClick={closeForm}
          >
            <h6 className="m-0">X</h6>
          </button>
        </div>
      </CCardHeader>

      <CRow className="custom-card-body">
        <CCol xs={12}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form className="p-4">
                <CRow>
                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="loanType">
                      Loan Type <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="text"
                      id="loanType"
                      name="loanType"
                      as={CFormInput}
                      placeholder="Loan Type"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="loanType"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="price">
                      Mobile Price <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="number"
                      id="price"
                      name="price"
                      as={CFormInput}
                      placeholder="Mobile Price"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="price"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="down_payment">
                      Down Payment <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="number"
                      id="down_payment"
                      name="down_payment"
                      as={CFormInput}
                      placeholder="Down Payment"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="down_payment"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="rate">
                      Rate <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="number"
                      id="rate"
                      name="rate"
                      as={CFormInput}
                      placeholder="Rate"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="rate"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  {/* ✅ NEW FIELD: is_active */}
                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="is_active">
                      Status <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      id="is_active"
                      name="is_active"
                      as={CFormSelect}
                      className="custom-form-input"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </Field>
                    <ErrorMessage
                      name="is_active"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  {/* ✅ NEW FIELD: file_charges */}
                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="file_charges">
                      File Charges <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="number"
                      id="file_charges"
                      name="file_charges"
                      as={CFormInput}
                      placeholder="File Charges"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="file_charges"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>

                  {/* ✅ NEW FIELD: other_charges */}
                  <CCol xs={6} className="position-relative custom-form-label">
                    <CFormLabel htmlFor="other_charges">
                      Other Charges <span className="red-star">*</span>
                    </CFormLabel>
                    <Field
                      type="number"
                      id="other_charges"
                      name="other_charges"
                      as={CFormInput}
                      placeholder="Other Charges"
                      className="custom-form-input"
                    />
                    <ErrorMessage
                      name="other_charges"
                      component="div"
                      className="text-danger"
                    />
                  </CCol>
                </CRow>

                <div className="identification_btn">
                  <CButton
                    type="submit"
                    className="custom-submit-btn"
                    disabled={isSubmitting}
                  >
                    {edit ? "Update" : "Submit"}
                  </CButton>
                </div>
              </Form>
            )}
          </Formik>
        </CCol>
      </CRow>
    </CRow>
  );
};

ProductAddForm.propTypes = {
  setShowAddForm: PropTypes.func.isRequired,
  setShowAddButton: PropTypes.func.isRequired,
  setShowSelectAndSearch: PropTypes.func.isRequired,
  intial: PropTypes.object,
  edit: PropTypes.bool,
};

export default ProductAddForm;